/**
 * Monetag — Rewarded Interstitial only (zone 12139997).
 *
 * Stays inside the Mini App WebView (native Telegram-message style banner).
 * - type: 'end'   → Rewarded Interstitial (in-app)
 * - type: 'pop'   → NEVER (opens external browser / leaves Mini App)
 * - type: 'inApp' → NEVER (forced auto ads, no reward)
 */

type MonetagShowOptions = {
  type?: 'end' | 'start' | 'preload' | 'pop' | 'inApp'
  ymid?: string
  requestVar?: string
}

type MonetagShowResult = {
  reward_event_type?: string
}

type MonetagShowFn = (opts?: MonetagShowOptions) => Promise<MonetagShowResult | void>

declare global {
  interface Window {
    show_12139997?: MonetagShowFn
  }
}

const SDK_WAIT_MS = 10_000

export type AdFailReason = 'sdk_missing' | 'non_valued' | 'verify_failed' | 'cancelled'

export type AdWatchResult =
  | { ok: true; source: 'monetag' }
  | { ok: false; source: 'cancelled'; reason: AdFailReason }

function wait(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms))
}

function getShowFn(): MonetagShowFn | undefined {
  const fn = window.show_12139997
  return typeof fn === 'function' ? fn : undefined
}

async function waitForMonetagSdk(timeoutMs = SDK_WAIT_MS): Promise<MonetagShowFn | null> {
  const existing = getShowFn()
  if (existing) return existing

  const started = Date.now()
  while (Date.now() - started < timeoutMs) {
    await wait(100)
    const fn = getShowFn()
    if (fn) return fn
  }
  return null
}

/** Keep Telegram Mini App expanded so Monetag can place the in-app interstitial. */
function prepareMiniAppShell() {
  const tg = window.Telegram?.WebApp
  if (!tg) return

  try {
    tg.ready()
  } catch {
    // ignore
  }
  try {
    tg.expand()
  } catch {
    // ignore
  }
  // Bot API 8+ — best-effort fullscreen inside Telegram client
  const anyTg = tg as typeof tg & { requestFullscreen?: () => void }
  try {
    anyTg.requestFullscreen?.()
  } catch {
    // ignore
  }
}

function telegramYmid(): string | undefined {
  prepareMiniAppShell()
  const id = window.Telegram?.WebApp?.initDataUnsafe?.user?.id
  return id != null ? String(id) : undefined
}

function fail(reason: AdFailReason): AdWatchResult {
  return { ok: false, source: 'cancelled', reason }
}

function isVerifyFailure(err: unknown): boolean {
  const message =
    typeof err === 'string'
      ? err
      : err instanceof Error
        ? err.message
        : typeof err === 'object' && err && 'message' in err
          ? String((err as { message: unknown }).message)
          : String(err ?? '')
  return /failed to verify/i.test(message)
}

/** User-facing copy for a failed rewarded ad attempt. */
export function adFailMessage(reason: AdFailReason): string {
  switch (reason) {
    case 'sdk_missing':
      return 'تبلیغ در دسترس نیست؛ دوباره امتحان کنید'
    case 'non_valued':
      return 'این تبلیغ جایزه نداشت؛ دوباره امتحان کنید'
    case 'verify_failed':
      return 'تأیید تبلیغ انجام نشد؛ دوباره امتحان کنید'
    case 'cancelled':
    default:
      return 'تبلیغ کامل نشد؛ دوباره امتحان کنید'
  }
}

/** Preload Rewarded Interstitial creatives (in-app only). */
export async function preloadRewardedAd(): Promise<boolean> {
  prepareMiniAppShell()
  const show = await waitForMonetagSdk(4000)
  if (!show) return false
  try {
    await show({ type: 'preload', ymid: telegramYmid(), requestVar: 'lucky-numbers' })
    return true
  } catch {
    return false
  }
}

/**
 * Show Monetag Rewarded Interstitial inside the Mini App WebView.
 * Never uses 'pop' (that leaves the Mini App).
 *
 * Grant when Monetag's Promise resolves (ad show confirmed), including
 * `non_valued` fallback creatives — otherwise users watch ads and get nothing.
 * Still refuse on SDK missing, verify timeout, or hard cancel/reject.
 */
export async function showRewardedAd(): Promise<AdWatchResult> {
  prepareMiniAppShell()

  const show = await waitForMonetagSdk()
  if (!show) {
    return fail('sdk_missing')
  }

  try {
    await show({
      type: 'end',
      ymid: telegramYmid(),
      requestVar: 'lucky-numbers',
    })
    return { ok: true, source: 'monetag' }
  } catch (err) {
    if (isVerifyFailure(err)) {
      return fail('verify_failed')
    }
    return fail('cancelled')
  }
}
